import { type DatabaseLayoutType } from '@databases/types/database/view';

import { type FieldConfiguration } from './configuration-parsers/parse-configuration';

export enum TokenType {
  FIELD = 'field',
  VIEW = 'view',
  LAYOUT = 'layout',
  ADD_FILTER = 'add_filter',
  EDIT_FILTER = 'edit_filter',
  REMOVE_FILTER = 'remove_filter',
  SORT = 'sort',
  ADD_ENTRY = 'add_entry',
  EDIT_ENTRY = 'edit_entry',
  REMOVE_ENTRY = 'remove_entry',
  VALUE = 'value',
  META = 'meta',
  EDIT_FIELD_SETTING = 'edit_field_setting',
  EDIT_VIEW_SETTING = 'edit_view_setting',
  EDIT_SORT = 'edit_sort',
  REMOVE_SORT = 'remove_sort',
  REMOVE_FIELD = 'remove_field',
  REMOVE_VIEW = 'remove_view',
  ADD_HIDDEN_FIELD = 'add_hidden_field',
  REMOVE_HIDDEN_FIELD = 'remove_hidden_field'
}

export const NO_METADATA_CHANGES_LINE = 'No metadata changes';

export enum DatabaseSortDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export interface FieldToken {
  tokenType: TokenType.FIELD;
  name: string;
  type: string;
  configuration?: FieldConfiguration;
  settings?: Record<string, any>;
}

export interface ViewToken {
  tokenType: TokenType.VIEW;
  name: string;
}

export interface LayoutToken {
  tokenType: TokenType.LAYOUT;
  type: DatabaseLayoutType;
  viewName: string;
  groupField?: string;
}

export enum Conjunction {
  OR = 'OR',
  AND = 'AND'
}

export interface AddFilterToken {
  tokenType: TokenType.ADD_FILTER;
  viewName: string;
  conjunction: Conjunction;
  filterOrFilterGroups: (Filter | FilterGroup)[];
}

export interface EditFilterToken {
  tokenType: TokenType.EDIT_FILTER;
  viewName: string;
  conjunction: Conjunction;
  filterOrFilterGroups: (Filter | FilterGroup)[];
}

export enum Verb {
  'IS',
  'CONTAINS',
  'IS_EMPTY',
  'IS_GREATER_THAN',
  'IS_GREATER_THAN_OR_EQUAL_TO',
  'IS_LESS_THAN',
  'IS_LESS_THAN_OR_EQUAL_TO'
}

export interface Operator {
  verb: Verb;
  negated: boolean;
}

export interface Filter {
  fieldName: string;
  operator: Operator;
  value: string;
}

export interface FilterGroup {
  conjunction: Conjunction;
  filters: Filter[];
}

export interface SortToken {
  tokenType: TokenType.SORT;
  fieldName: string;
  viewName: string;
  direction?: DatabaseSortDirection;
}

export interface AddEntryToken {
  tokenType: TokenType.ADD_ENTRY;
  rowNumber?: string;
}

export interface EditEntryToken {
  tokenType: TokenType.EDIT_ENTRY;
  rowNumber: string;
}

export interface RemoveEntryToken {
  tokenType: TokenType.REMOVE_ENTRY;
  rowNumber: string;
}

export interface ValueToken {
  tokenType: TokenType.VALUE;
  fieldName: string;
  value: any;
  resetValue?: boolean;
}

export interface MetaToken {
  tokenType: TokenType.META;
  defaultView: string;
}

export interface EditFieldSettingToken {
  tokenType: TokenType.EDIT_FIELD_SETTING;
  fieldName: string;
  property: string;
  value: string | boolean | FieldConfiguration;
}

export interface EditViewSettingToken {
  tokenType: TokenType.EDIT_VIEW_SETTING;
  viewName: string;
  property: string;
  value: string;
}

export interface EditSortToken {
  tokenType: TokenType.EDIT_SORT;
  viewName: string;
  fieldName: string;
  direction: DatabaseSortDirection;
}
export interface RemoveSortToken {
  tokenType: TokenType.REMOVE_SORT;
  viewName: string;
  fieldName: string;
  direction?: DatabaseSortDirection;
}

export interface RemoveFieldToken {
  tokenType: TokenType.REMOVE_FIELD;
  fieldName: string;
}

export interface RemoveViewToken {
  tokenType: TokenType.REMOVE_VIEW;
  viewName: string;
}

export interface RemoveFilterToken {
  tokenType: TokenType.REMOVE_FILTER;
  viewName: string;
}

export interface AddHiddenFieldToken {
  tokenType: TokenType.ADD_HIDDEN_FIELD;
  viewName: string;
  fieldNames: string[];
}

export interface RemoveHiddenFieldToken {
  tokenType: TokenType.REMOVE_HIDDEN_FIELD;
  viewName: string;
  fieldNames: string[];
}

export type Token =
  | FieldToken
  | ViewToken
  | LayoutToken
  | AddFilterToken
  | EditFilterToken
  | SortToken
  | AddEntryToken
  | EditEntryToken
  | RemoveEntryToken
  | ValueToken
  | MetaToken
  | EditFieldSettingToken
  | EditViewSettingToken
  | EditSortToken
  | RemoveSortToken
  | RemoveFieldToken
  | RemoveViewToken
  | RemoveFilterToken
  | AddHiddenFieldToken
  | RemoveHiddenFieldToken;

export type OnTokenHandler = (token: Token) => void;

export type DataValueStrategy =
  | { kind: 'positional' }
  | {
      kind: 'single-target';
      targetFieldName: string;
    };

export type StreamingCsvParserOptions = {
  dataValueStrategy?: DataValueStrategy;
};

export enum PARSER_STATES {
  INIT = 'INIT',
  FIELDS = 'FIELDS',
  VIEWS = 'VIEWS',
  ENTRIES = 'ENTRIES',
  METADATA_OPERATIONS = 'METADATA_OPERATIONS',
  DATA_OPERATIONS = 'DATA_OPERATIONS'
}

export const CSV_MARKER = 'MARKER';

export const FILTERED_VALUE_KEYS = ['operation_type', 'entry_index'] as const;

type FieldKeys = 'field_name' | 'type' | 'configuration';
type ViewKeys = 'view_name' | 'layout' | 'sorts' | 'hidden_fields';
type OperationKeys = 'operation_type' | 'entry_index';
type MetadataKeys = 'operation_type' | 'target' | 'property' | 'value';
type FilterKeys = 'filters';
type Keys = FieldKeys | ViewKeys | OperationKeys | MetadataKeys | FilterKeys;
export type ParsedRow = Record<Keys, string>;
